import { Sparkles } from 'lucide-react';
import React from 'react';
import {
  isFeatureCoachCompletedForProduct,
  markFeatureCoachCompletedForProduct,
  markFeatureCoachSuppressAll,
} from '../../shared/onboarding/onboardingState';

/** @deprecated Use isFeatureCoachCompletedForProduct from onboardingState or pass productId */
export function isFeatureCoachCompleted(featureKey: string, productId?: string): boolean {
  return isFeatureCoachCompletedForProduct(featureKey, productId);
}

/** @deprecated Use markFeatureCoachCompletedForProduct */
export function markFeatureCoachCompleted(featureKey: string, productId?: string): void {
  markFeatureCoachCompletedForProduct(featureKey, productId);
}

export type FeatureCoachProps = {
  featureKey: string;
  /** When set, dismissal is stored per org product (v2 keys). */
  productId?: string;
  theme: 'light' | 'dark';
  show: boolean;
  title: string;
  children: React.ReactNode;
  onDismiss?: () => void;
  /** Optional secondary CTA (e.g. scroll to a section) */
  actionLabel?: string;
  onAction?: () => void;
  /** Default primary label (default: Got it) */
  primaryButtonLabel?: string;
  /** If set, primary button runs this instead of marking coach complete + onDismiss */
  onPrimaryButtonClick?: () => void;
  /** When true, primary button is disabled (e.g. tour edu-guard on filters step). */
  primaryButtonDisabled?: boolean;
  /** Optional back (e.g. multi-step tour); rendered as secondary, before primary */
  backButtonLabel?: string;
  onBackButtonClick?: () => void;
  /** Optional skip / secondary dismiss (e.g. skip entire journey) */
  skipButtonLabel?: string;
  onSkipButtonClick?: () => void;
  /** Announce updates to assistive tech (default polite for journey coaches) */
  role?: 'region' | 'status';
  'aria-live'?: 'off' | 'polite' | 'assertive';
  /** e.g. "Step 2 of 5" — shown next to the title */
  stepLabel?: string;
  /** Small line above the title (e.g. "Guided tour") to separate from other on-page hints */
  kicker?: string;
  /** Shows a tertiary control to hide this coach for every product (product-scoped keys only). */
  showSuppressNewProductsLink?: boolean;
  /** Optional second skip (e.g. skip Releases guide for all products in org). */
  escapeHatchButtonLabel?: string;
  onEscapeHatchClick?: () => void;
  /** Visual variant: `tour` uses indigo gradient + CSS in index.css (no green inline styles). */
  variant?: 'default' | 'tour';
  /** Rendered after kicker, before title (e.g. progress dots). */
  headerExtra?: React.ReactNode;
};

/**
 * Lightweight onboarding callout (localStorage dismissal). No tour library.
 */
export const FeatureCoach: React.FC<FeatureCoachProps> = ({
  featureKey,
  productId,
  theme,
  show,
  title,
  children,
  onDismiss,
  actionLabel,
  onAction,
  primaryButtonLabel = 'Got it',
  onPrimaryButtonClick,
  primaryButtonDisabled = false,
  backButtonLabel,
  onBackButtonClick,
  skipButtonLabel,
  onSkipButtonClick,
  role: roleProp = 'region',
  'aria-live': ariaLive = 'off',
  stepLabel,
  kicker,
  showSuppressNewProductsLink = false,
  escapeHatchButtonLabel,
  onEscapeHatchClick,
  variant = 'default',
  headerExtra,
}) => {
  if (!show) return null;
  const isLight = theme === 'light';
  const isTour = variant === 'tour';

  const handlePrimary = () => {
    if (onPrimaryButtonClick) {
      onPrimaryButtonClick();
      return;
    }
    markFeatureCoachCompletedForProduct(featureKey, productId);
    onDismiss?.();
  };

  const handleSuppressNewProducts = () => {
    if (productId) {
      markFeatureCoachSuppressAll(featureKey);
    }
    onDismiss?.();
  };

  return (
    <div
      className={isTour ? 'feature-coach forecast-tour atlas-tour' : 'feature-coach'}
      data-theme={theme}
      role={roleProp}
      aria-live={ariaLive}
      aria-label={[kicker, title, stepLabel].filter(Boolean).join('. ') || title}
      style={
        isTour
          ? {
              marginBottom: '20px',
              padding: '16px 18px',
              borderRadius: '12px',
            }
          : {
              marginBottom: '20px',
              padding: '16px 18px',
              borderRadius: '12px',
              border: `1px solid ${isLight ? 'rgba(34, 197, 94, 0.35)' : 'rgba(74, 222, 128, 0.25)'}`,
              backgroundColor: isLight ? 'rgba(34, 197, 94, 0.08)' : 'rgba(34, 197, 94, 0.1)',
              color: isLight ? '#14532d' : '#bbf7d0',
            }
      }
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
        <Sparkles
          size={20}
          className={isTour ? 'feature-coach-tour-icon' : undefined}
          style={{ flexShrink: 0, marginTop: '2px' }}
          aria-hidden
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          {kicker ? (
            <div
              style={{
                fontSize: '11px',
                fontWeight: 600,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                opacity: isLight ? 0.75 : 0.85,
                marginBottom: '6px',
              }}
            >
              {kicker}
            </div>
          ) : null}
          {headerExtra ? (
            <div
              className={isTour ? 'feature-coach-header-extra' : undefined}
              style={{ marginBottom: '10px' }}
            >
              {headerExtra}
            </div>
          ) : null}
          <div
            style={{
              fontWeight: 700,
              fontSize: '15px',
              marginBottom: '8px',
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'baseline',
              gap: '8px',
            }}
          >
            <span>{title}</span>
            {stepLabel ? (
              <span
                style={{
                  fontWeight: 500,
                  fontSize: '12px',
                  opacity: 0.75,
                }}
              >
                {stepLabel}
              </span>
            ) : null}
          </div>
          <div style={{ fontSize: '14px', lineHeight: 1.55, opacity: 0.95 }}>{children}</div>
          <div
            style={{
              marginTop: '14px',
              display: 'flex',
              flexWrap: 'wrap',
              gap: '10px',
              alignItems: 'center',
            }}
          >
            {skipButtonLabel && onSkipButtonClick ? (
              <button
                type="button"
                className="btn btn-secondary"
                style={{ padding: '8px 18px', fontSize: '13px' }}
                onClick={onSkipButtonClick}
              >
                {skipButtonLabel}
              </button>
            ) : null}
            {escapeHatchButtonLabel && onEscapeHatchClick ? (
              <button
                type="button"
                className="btn btn-secondary"
                style={{ padding: '8px 18px', fontSize: '13px' }}
                onClick={onEscapeHatchClick}
              >
                {escapeHatchButtonLabel}
              </button>
            ) : null}
            {actionLabel && onAction ? (
              <button
                type="button"
                className="btn btn-secondary"
                style={{ padding: '8px 18px', fontSize: '13px' }}
                onClick={onAction}
              >
                {actionLabel}
              </button>
            ) : null}
            {backButtonLabel && onBackButtonClick ? (
              <button
                type="button"
                className="btn btn-secondary"
                style={{ padding: '8px 18px', fontSize: '13px' }}
                onClick={onBackButtonClick}
              >
                {backButtonLabel}
              </button>
            ) : null}
            <button
              type="button"
              className="btn btn-primary"
              style={{ padding: '8px 18px', fontSize: '13px' }}
              onClick={handlePrimary}
              disabled={primaryButtonDisabled}
            >
              {primaryButtonLabel}
            </button>
            {showSuppressNewProductsLink && productId ? (
              <button
                type="button"
                className="btn btn-link"
                style={{
                  padding: '8px 12px',
                  fontSize: '13px',
                  textDecoration: 'underline',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'inherit',
                  opacity: 0.9,
                }}
                onClick={handleSuppressNewProducts}
              >
                Don&apos;t show for new products
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};
